/**
 * Backwards-compat shim for legacy Deep Explorer chats — Phase 8.2.
 *
 * Old chats persist their deliverable as a virtual file (`/deliverable.md`)
 * in the deep agent's tool runtime. The Workspace contract (Phase 1.x onward)
 * expects every assistant turn to emit a typed `document` artifact instead.
 *
 * On chat hydration, when the persisted assistant message lacks any
 * `document` artifact in `context.artifacts` but the LangGraph checkpoint
 * still surfaces a `/deliverable.md` file, we synthesise an artifact
 * client-side so the WorkspaceLayout can render it.
 *
 * Idempotency: the synthesised id derives deterministically from the
 * `turnId`, so repeated hydration cycles produce the same artifact id and
 * never accumulate duplicates in the store.
 *
 * Spec: `.workspace-spec/WORKSPACE_CONTRACT.md` (Persistence schema, "Out of
 * scope: Migration of persisted old chats — transitional shim only").
 */
import type { Artifact, DocumentPayload } from "@/api/models"

export interface DeliverableSource {
  /** Markdown body of the legacy `/deliverable.md` file. */
  body: string
  /** Conversation (session) id the artifact belongs to. */
  conversationId: string
  /**
   * Stable per-turn id. For legacy chats the frontend uses the assistant
   * message's `messageId` if available, otherwise falls back to the session
   * id. Either choice is fine — the only invariant we need is "same input ⇒
   * same artifact id" so re-hydration is idempotent.
   */
  turnId: string
  /** ISO 8601 timestamp to stamp on the synthesised artifact. */
  createdAt: string
}

/** Stable id prefix so live `_build_deliverable_artifact` emissions can never
 *  collide with the synthesised legacy ones. */
const LEGACY_ID_PREFIX = "legacy-deliverable-"

/**
 * Synthesise a `document` artifact from a legacy `/deliverable.md` body.
 *
 * The synthesised artifact's id is `${LEGACY_ID_PREFIX}${turnId}` so the
 * mapping is deterministic across re-hydration cycles. Title is taken from
 * the first markdown H1 in the body, falling back to "Deliverable" when no
 * H1 is present.
 */
export function synthesizeDocumentFromDeliverable(source: DeliverableSource): Artifact {
  const trimmed = source.body.trim()
  const wordCount = trimmed.length === 0 ? 0 : trimmed.split(/\s+/).filter(Boolean).length
  // ~200 wpm is the canonical "average reading speed" used elsewhere in the
  // contract (DocumentPayload.estimated_read_minutes). Floor at 1 so even a
  // one-paragraph deliverable doesn't render "0 min read".
  const estimatedReadMinutes = Math.max(1, Math.round(wordCount / 200))

  const titleMatch = source.body.match(/^\s*#\s+(.+?)\s*$/m)
  const title = titleMatch?.[1]?.trim() || "Deliverable"

  const payload: DocumentPayload = {
    type: "document",
    format: "markdown",
    body: source.body,
    word_count: wordCount,
    estimated_read_minutes: estimatedReadMinutes,
  }

  return {
    id: `${LEGACY_ID_PREFIX}${source.turnId}`,
    version: 1,
    parent_id: null,
    refines_artifact_id: null,
    conversation_id: source.conversationId,
    turn_id: source.turnId,
    created_at: source.createdAt,
    type: "document",
    payload,
    title,
    citations: [],
    trace_ref: { mission_id: source.turnId },
    actions: [],
    is_latest: true,
  }
}

/** Predicate: was this artifact produced by the legacy migration shim?
 *  Useful for renderers that want to badge or hide legacy synthesised
 *  documents differently from real ones. */
export function isLegacyDeliverableArtifact(artifact: Artifact): boolean {
  return artifact.id.startsWith(LEGACY_ID_PREFIX)
}
